// app/javascript/controllers/dae20/tmc_monitor_controller.js
import { Controller } from "@hotwired/stimulus"

/**
 * Stimulus Controller для компонента tmc_monitor.
 *
 * Функции:
 * - Загрузка сравнения текущих данных с выбранным снимком
 * - Select для переключения между снимками
 * - Truncate длинных значений с popup для полного просмотра
 * - История изменений поля по клику (field_history popup)
 */
export default class extends Controller {
  static targets = ["content", "snapshotInfo", "title", "snapshotSelect"]

  static values = {
    componentId: String,
    tmcUid: String,
    snapshotId: String
  }

  // Текущий выбранный snapshot_id (пустая строка = последний)
  selectedSnapshotId = ""

  // Резолвленный tmc_uid (из ответа compare, актуален для обоих режимов)
  _resolvedTmcUid = ""

  get csrfToken() {
    const meta = document.querySelector('meta[name="csrf-token"]')
    return meta ? meta.content : ""
  }

  connect() {
    this._ensureHistoryDialog()
    this._ensureLongTextDialog()
    if (this.tmcUidValue || this.snapshotIdValue) {
      this.loadComparison(this.snapshotIdValue || "")
    }
  }

  async loadComparison(snapshotId = "") {
    if (!this.hasContentTarget) return

    this.selectedSnapshotId = snapshotId

    try {
      let url = `/dae20/tmc_monitors/${this.componentIdValue}/compare?tmc_uid=${encodeURIComponent(this.tmcUidValue || "")}`
      if (snapshotId) {
        url += `&snapshot_id=${encodeURIComponent(snapshotId)}`
      }

      const response = await fetch(url, {
        headers: { "Accept": "application/json", "X-CSRF-Token": this.csrfToken },
        credentials: "same-origin"
      })
      const data = await response.json()

      if (data.ok) {
        this.renderComparison(data)
      } else {
        this.contentTarget.innerHTML = `<div class="p-4 text-red-600 text-sm">Ошибка: ${(data.errors || []).join(", ")}</div>`
      }
    } catch (error) {
      this.contentTarget.innerHTML = `<div class="p-4 text-red-600 text-sm">Сетевая ошибка: ${error.message}</div>`
    }
  }

  // ── Snapshot Select ───────────────────────────────────────────────

  onSnapshotSelectChange(event) {
    this.loadComparison(event.target.value)
  }

  populateSnapshotSelect(snapshots, currentSnapshotId) {
    if (!this.hasSnapshotSelectTarget) return

    const select = this.snapshotSelectTarget
    select.innerHTML = ""

    // Первая опция — последний снимок
    const defaultOpt = document.createElement("option")
    defaultOpt.value = ""
    defaultOpt.textContent = "Последний снимок"
    select.appendChild(defaultOpt)

    for (const snap of snapshots) {
      const opt = document.createElement("option")
      opt.value = snap.id
      opt.textContent = `${snap.name}${snap.author ? ` (${snap.author})` : ""}`
      if (String(snap.id) === String(currentSnapshotId)) {
        opt.selected = true
      }
      select.appendChild(opt)
    }

    select.classList.remove("hidden")
  }

  // ── Render Comparison Table ───────────────────────────────────────

  renderComparison(data) {
    const { tmc_uid, tmc_name, snapshot_name, snapshot_date, snapshot_author, snapshot_id, snapshots, fields } = data

    // Сохраняем резолвленный uid для field_history
    if (tmc_uid) {
      this._resolvedTmcUid = tmc_uid
    }

    // Title
    if (this.hasTitleTarget && tmc_name) {
      this.titleTarget.textContent = `Сравнение: ${tmc_name}`
    }

    // Snapshot info
    if (this.hasSnapshotInfoTarget) {
      if (snapshot_name) {
        this.snapshotInfoTarget.textContent = `${snapshot_name} (${snapshot_date || ""})`
        this.snapshotInfoTarget.className = "text-xs text-green-600"
      } else {
        this.snapshotInfoTarget.textContent = "Снимок не найден"
        this.snapshotInfoTarget.className = "text-xs text-orange-500"
      }
    }

    // Populate snapshot select
    if (snapshots && snapshots.length > 0) {
      this.populateSnapshotSelect(snapshots, snapshot_id)
    }

    // Build comparison table
    let html = `<table class="w-full text-xs">`
    html += `<thead class="sticky top-0 bg-gray-50 z-10">`
    html += `<tr class="border-b border-gray-200">`
    html += `<th class="px-3 py-2 text-left text-gray-600 font-medium" style="width:24%">Поле</th>`
    html += `<th class="px-3 py-2 text-left text-gray-600 font-medium" style="width:32%">Снимок</th>`
    html += `<th class="px-3 py-2 text-left text-gray-600 font-medium" style="width:32%">Текущее</th>`
    html += `<th class="px-3 py-2 text-center text-gray-600 font-medium" style="width:12%">Статус</th>`
    html += `</tr></thead><tbody>`

    for (const field of fields) {
      const statusClass = this._statusClass(field.status)
      const statusLabel = this._statusLabel(field.status)
      const rowBg = field.status === "changed" ? "bg-yellow-50" : field.status === "new" ? "bg-blue-50" : ""

      const snapshotCell = this._renderCellValue(field.snapshot_value, field.name)
      const currentCell = this._renderCellValue(field.current_value, field.name)

      // Кнопка истории рядом с названием поля
      const historyBtn = `<button type="button"
        class="ml-1.5 inline-flex items-center justify-center w-5 h-5 rounded-md text-blue-400 hover:text-blue-600 hover:bg-blue-50 transition-colors align-middle"
        title="История изменений"
        data-action="click->dae20--tmc-monitor#showFieldHistory"
        data-field-name="${this._escapeAttr(field.name)}">
        <svg class="w-4 h-4" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"/>
        </svg>
      </button>`

      html += `<tr class="border-b border-gray-100 ${rowBg} hover:bg-gray-50">`
      html += `<td class="px-3 py-1.5 font-medium text-gray-700 align-top whitespace-nowrap">${this._esc(field.name)}${historyBtn}</td>`
      html += `<td class="px-3 py-1.5 text-gray-500 align-top break-words" style="max-width:250px">${snapshotCell}</td>`
      html += `<td class="px-3 py-1.5 text-gray-800 align-top break-words" style="max-width:250px">${currentCell}</td>`
      html += `<td class="px-3 py-1.5 text-center align-top"><span class="inline-block px-1.5 py-0.5 rounded text-[10px] font-medium ${statusClass}">${statusLabel}</span></td>`
      html += `</tr>`
    }

    html += `</tbody></table>`
    this.contentTarget.innerHTML = html
  }

  // ── Cell Value Rendering (truncate) ───────────────────────────────

  _renderCellValue(text, fieldName) {
    if (!text || text === "—" || text.trim() === "") {
      return `<span class="text-gray-300">—</span>`
    }

    const LIMIT = 120
    if (text.length <= LIMIT) {
      return this._esc(text)
    }

    // Truncate + кнопка показать
    const truncated = this._esc(text.substring(0, LIMIT)) + "..."
    const btnId = `lt-${Math.random().toString(36).substring(2, 8)}`
    return `${truncated}
      <button type="button"
        class="ml-1 text-blue-500 hover:text-blue-700 text-[10px] font-medium whitespace-nowrap"
        data-action="click->dae20--tmc-monitor#showLongText"
        data-full-text="${this._escapeAttr(text)}"
        data-field-name="${this._escapeAttr(fieldName)}">
        <svg class="w-3 h-3 inline" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0zM2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"/>
        </svg>
        (${text.length})
      </button>`
  }

  // ── Long Text Dialog ──────────────────────────────────────────────

  showLongText(event) {
    const btn = event.currentTarget
    const text = btn.dataset.fullText
    const fieldName = btn.dataset.fieldName

    const dialog = this._longTextDialog
    dialog.querySelector("[data-role=title]").textContent = fieldName || "Содержание"
    dialog.querySelector("[data-role=body]").textContent = text
    dialog.querySelector("[data-role=footer]").textContent = `${(text || "").length} символов`
    dialog.showModal()
  }

  _ensureLongTextDialog() {
    if (this._longTextDialog) return

    const dialog = document.createElement("dialog")
    dialog.className = "fixed m-auto rounded-lg shadow-2xl max-w-3xl w-[90vw] max-h-[85vh] p-0 backdrop:bg-black/40"
    dialog.innerHTML = `
      <div class="flex flex-col max-h-[85vh]">
        <div class="flex items-center justify-between px-4 py-2 border-b border-gray-200 bg-gray-50 shrink-0 rounded-t-lg">
          <h3 class="text-sm font-medium text-gray-700" data-role="title"></h3>
          <button type="button" class="w-6 h-6 flex items-center justify-center text-gray-400 hover:text-gray-700 text-lg" data-action="click->dae20--tmc-monitor#closeLongText">×</button>
        </div>
        <div class="p-4 overflow-auto flex-1 whitespace-pre-wrap break-words text-xs text-gray-800 font-mono" data-role="body"></div>
        <div class="px-4 py-2 border-t border-gray-200 bg-gray-50 shrink-0 text-[10px] text-gray-400 rounded-b-lg" data-role="footer"></div>
      </div>`
    dialog.addEventListener("click", (e) => {
      if (e.target === dialog) dialog.close()
    })
    this.element.appendChild(dialog)
    this._longTextDialog = dialog
  }

  closeLongText() {
    this._longTextDialog?.close()
  }

  // ── Field History Dialog ──────────────────────────────────────────

  async showFieldHistory(event) {
    const btn = event.currentTarget
    const fieldName = btn.dataset.fieldName
    if (!fieldName) return

    const dialog = this._historyDialog
    dialog.querySelector("[data-role=title]").textContent = `История: ${fieldName}`
    dialog.querySelector("[data-role=body]").innerHTML =
      `<div class="text-center text-gray-400 py-4">Загрузка...</div>`
    dialog.showModal()

    try {
      const url = `/dae20/tmc_monitors/${this.componentIdValue}/field_history?tmc_uid=${encodeURIComponent(this._resolvedTmcUid || this.tmcUidValue)}&field_name=${encodeURIComponent(fieldName)}`
      const response = await fetch(url, {
        headers: { "Accept": "application/json", "X-CSRF-Token": this.csrfToken },
        credentials: "same-origin"
      })
      const data = await response.json()

      if (data.ok) {
        this._renderFieldHistory(dialog, data.history, fieldName)
      } else {
        dialog.querySelector("[data-role=body]").innerHTML =
          `<div class="text-red-500 text-sm p-4">Ошибка загрузки истории</div>`
      }
    } catch (e) {
      dialog.querySelector("[data-role=body]").innerHTML =
        `<div class="text-red-500 text-sm p-4">Сетевая ошибка: ${e.message}</div>`
    }
  }

  _renderFieldHistory(dialog, history, fieldName) {
    const body = dialog.querySelector("[data-role=body]")

    if (!history || history.length === 0) {
      body.innerHTML = `<div class="text-center text-gray-400 py-4">Нет снимков для этого поля</div>`
      return
    }

    let html = `<div class="space-y-3">`

    for (let i = 0; i < history.length; i++) {
      const item = history[i]
      const prevItem = i < history.length - 1 ? history[i + 1] : null
      const value = item.value ?? "(не зафиксировано)"
      const prevValue = prevItem?.value

      // Определяем изменение
      let changeIndicator = ""
      if (i === history.length - 1) {
        changeIndicator = `<span class="text-[10px] text-blue-500 font-medium">первый снимок</span>`
      } else if (value !== prevValue) {
        changeIndicator = `<span class="text-[10px] text-yellow-600 font-medium">изменено</span>`
      } else {
        changeIndicator = `<span class="text-[10px] text-green-600">без изменений</span>`
      }

      const valueDisplay = value && String(value).length > 200
        ? `<div class="mt-1 text-xs text-gray-700 bg-white rounded border border-gray-100 px-2 py-1 max-h-24 overflow-auto whitespace-pre-wrap font-mono">${this._esc(String(value).substring(0, 200))}...<br><span class="text-[10px] text-gray-400">(${String(value).length} симв.)</span></div>`
        : `<div class="mt-1 text-xs text-gray-700 bg-white rounded border border-gray-100 px-2 py-1 whitespace-pre-wrap">${this._esc(String(value))}</div>`

      html += `
        <div class="border-l-2 ${value !== prevValue && i < history.length - 1 ? 'border-yellow-400' : 'border-gray-200'} pl-3">
          <div class="flex items-center gap-2 text-xs">
            <span class="font-medium text-gray-700">${this._esc(item.snapshot_name)}</span>
            <span class="text-gray-400">${this._esc(item.date)}</span>
            ${item.author ? `<span class="text-gray-500">${this._esc(item.author)}</span>` : ""}
            ${changeIndicator}
          </div>
          ${item.comment ? `<div class="text-[10px] text-blue-400 italic mt-0.5">${this._esc(item.comment)}</div>` : ""}
          ${valueDisplay}
          ${value !== prevValue && prevValue != null ? `<div class="text-[10px] text-gray-400 mt-0.5">было: ${this._esc(String(prevValue).substring(0, 100))}${String(prevValue).length > 100 ? "..." : ""}</div>` : ""}
        </div>`
    }

    html += `</div>`
    body.innerHTML = html
  }

  _ensureHistoryDialog() {
    if (this._historyDialog) return

    const dialog = document.createElement("dialog")
    // fixed + m-auto центрирует dialog; max-h + overflow-auto даёт скролл внутри body
    dialog.className = "fixed m-auto rounded-lg shadow-2xl max-w-2xl w-[85vw] max-h-[85vh] p-0 backdrop:bg-black/40"
    dialog.innerHTML = `
      <div class="flex flex-col max-h-[85vh]">
        <div class="flex items-center justify-between px-4 py-2 border-b border-gray-200 bg-gray-50 shrink-0 rounded-t-lg">
          <h3 class="text-sm font-medium text-gray-700" data-role="title">История поля</h3>
          <button type="button" class="w-6 h-6 flex items-center justify-center text-gray-400 hover:text-gray-700 text-lg" data-action="click->dae20--tmc-monitor#closeHistory">×</button>
        </div>
        <div class="p-4 overflow-auto flex-1" data-role="body"></div>
      </div>`
    // Закрытие по клику вне dialog (на backdrop)
    dialog.addEventListener("click", (e) => {
      if (e.target === dialog) dialog.close()
    })
    this.element.appendChild(dialog)
    this._historyDialog = dialog
  }

  closeHistory() {
    this._historyDialog?.close()
  }

  // ── Helpers ───────────────────────────────────────────────────────

  _statusClass(status) {
    switch (status) {
      case "changed": return "bg-yellow-100 text-yellow-800"
      case "new": return "bg-blue-100 text-blue-800"
      case "match": return "bg-green-100 text-green-800"
      default: return "bg-gray-100 text-gray-600"
    }
  }

  _statusLabel(status) {
    switch (status) {
      case "changed": return "изменено"
      case "new": return "новое"
      case "match": return "совпадает"
      default: return status
    }
  }

  _esc(text) {
    const div = document.createElement("div")
    div.textContent = text
    return div.innerHTML
  }

  _escapeAttr(text) {
    return String(text || "")
      .replace(/&/g, "&amp;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#39;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
  }
}
